import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { dataApiClient } from '../client'
import { staticCacheConfig, createCacheKey, QueryOptions } from '../../../lib/utils'
import type { SpecimenByFilterRequest, SpecimenByIdRequest, SpecimenResponse } from '../'

/**
 * Hook to fetch specimens with filtering
 */
export function useSpecimens(
  request: SpecimenByFilterRequest,
  options?: QueryOptions<SpecimenResponse, Error, SpecimenByFilterRequest>
) {
  return useQuery({
    queryKey: createCacheKey('specimens', request),
    queryFn: async () => {
      const { data, error } = await dataApiClient.POST('/specimen/get-by-filter', {
        body: request,
      })

      if (error) {
        throw new Error(`Failed to fetch specimens: ${error}`)
      }

      return data
    },
    ...staticCacheConfig,
    ...options,
  })
}

/**
 * Hook to fetch specimens with infinite scrolling support
 */
export function useInfiniteSpecimens(
  baseRequest: Omit<SpecimenByFilterRequest, 'page'>,
  options?: {
    enabled?: boolean
    staleTime?: number
    gcTime?: number
  }
) {
  return useInfiniteQuery({
    queryKey: createCacheKey('specimens-infinite', { ...baseRequest }),
    queryFn: async ({ pageParam = 1 }) => {
      const request: SpecimenByFilterRequest = {
        ...baseRequest,
        page: pageParam,
        page_size: baseRequest.page_size,
      }

      const { data, error } = await dataApiClient.POST('/specimen/get-by-filter', {
        body: request,
      })

      if (error) throw new Error(`Failed to fetch specimens: ${error}`)

      return data
    },
    getNextPageParam: lastPage => {
      if (!lastPage || lastPage.specimens.length < baseRequest.page_size) return undefined

      if (lastPage.current_page >= lastPage.total_pages) return undefined

      return lastPage.current_page + 1
    },
    initialPageParam: 1,
    ...staticCacheConfig,
    ...options,
  })
}

/**
 * Hook to prefetch specimens data
 */
export function usePrefetchSpecimens() {
  const queryClient = useQueryClient()

  return (request: SpecimenByFilterRequest) => {
    queryClient.prefetchQuery({
      queryKey: createCacheKey('specimens', request),
      queryFn: async () => {
        const { data, error } = await dataApiClient.POST('/specimen/get-by-filter', {
          body: request,
        })

        if (error) {
          throw new Error(`Failed to prefetch specimens: ${error}`)
        }

        return data
      },
      staleTime: staticCacheConfig.staleTime,
    })
  }
}

/**
 * Hook to fetch specimens by their IDs
 */
export function useSpecimensById(
  request: SpecimenByIdRequest,
  options?: QueryOptions<SpecimenResponse, Error, SpecimenByIdRequest>
) {
  return useQuery({
    queryKey: createCacheKey('specimens-by-id', request),
    queryFn: async () => {
      const { data, error } = await dataApiClient.POST('/specimen/get-by-id', {
        body: request,
      })

      if (error) throw new Error(`Failed to fetch specimens by ID: ${error}`)

      return data
    },
    ...staticCacheConfig,
    ...options,
  })
}

/**
 * Utility hook to invalidate specimens cache
 */
export function useInvalidateSpecimens() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['specimens'],
    })
  }
}
